// Defined in general.js
/* global dcAnalytics loader Uri userType userId */
import PropTypes from 'prop-types';
import React from 'react';
import { shuffleAndTruncate } from '../common/arrayUtils.js';
import NewDonorHome from './NewDonorHome.js';
import ExistingDonorHome from './ExistingDonorHome.js';
import { useUserInfo } from '../common/user/useUserInfo.js';
import { fetchProjects } from './fetchProjects.js';
import { useLocation } from '../common/location/useLocation.js';
import { ViewTrackingContext } from '../common/ViewTrackingContext.js';
import { sortQueryParams } from '../adapters/dupes/queryParams.js';
import { MaybeLeadGenWidget } from '../emailSignup/MaybeLeadGenWidget.tsx';

const normalizeParams = function (url) {
    const params = new Uri(url).deleteQueryParam('max')
        .deleteQueryParam('alternativeUrgency') // This is needed to make an A/B test work
        .deleteQueryParam('sortVersion') // This is needed to make an A/B test work
        .query().replace(/%20/g, '+');
    return sortQueryParams(params);
};

// Used by TrackSectionView.js and HomeProjectSection.js
const trackSectionView = function (sectionName) {
    dcAnalytics.track('Section viewed', {
        sectionName, pageName, userId,
    });
};

let HomeController = function (props) {
    const [projectData, setProjectData] = React.useState({});
    const [alternateHomeSection, setAlternateHomeSection] = React.useState();
    const [user] = useUserInfo();
    const [detectedLocation] = useLocation();

    const MAX_PROJECTS = 3;

    const getProjects = React.useCallback(
        url => projectData[normalizeParams(url)],
        [projectData],
    );

    const loadProjects = React.useCallback(function (url) {
        if (getProjects(url)) {
            // Already loaded
            return;
        }

        /**
         * Updates the component's state with project data
         * @param {string} url The search URL for the projects
         * @param {object[]} data The list of project data
         */
        const addProjectData = function (url, data) {
            const key = normalizeParams(url);
            data = shuffleAndTruncate(data, MAX_PROJECTS);

            // This has to use the lambda form instead of passing literal new data
            // Since useState doesn't merge, just directly passing the new object results in a race condition
            // where previous data can be overwritten
            setProjectData(oldProjects =>
                Object.assign({}, oldProjects, {
                    [key]: data,
                }),
            );
        };

        fetchProjects(url,
            (data) => {
                // Track when we expand the scope of the search based on the user's location
                if (data.expandedLocationMessage) {
                    dcAnalytics.track('Location search expanded', {
                        expansionMessage: data.expandedLocationMessage,
                        searchURL: data.searchURL,
                        pageName,
                    });
                }

                // Just in case updateIndex is lagging
                const eligibleProjects = data.proposals.filter(function (project) {
                    return project.fundingStatus === 'needs funding';
                });

                addProjectData(data.searchURL, eligibleProjects);
            },
            () => {
                addProjectData(url, []);
            },
        );
    }, [getProjects]);

    React.useEffect(() => {
        // If user is undefined, we don't know yet whether the user is logged in
        if (user !== undefined) {
            dcAnalytics.track('homePage', {
                userType: (user && user.userType) || 'new',
            });
        }
    }, [user]);

    React.useEffect(() => {
        const reloadTeacherPage = function () {
            if (userType === 'teacher') {
                loader.showPageLoaderAndShield();
                // reload the page so it will be forced to hit Fastly, which will redirect to the teacher hub for logged-in teachers
                window.location.reload();
            }
        };

        $(window).on('signinTriggered', reloadTeacherPage);

        return () => {
            $(window).off('signinTriggered', reloadTeacherPage);
        };
    }, []);

    React.useEffect(() => {
        // For A/B tests
        window.setAlternateHomeSection = setAlternateHomeSection;
    }, []);

    let HomeLayout = NewDonorHome;

    // Only show a customized page if the donor is logged in or cookied
    if (user && user.userId) {
        const userType = user.userType;

        if (userType === 'donor') {
            HomeLayout = ExistingDonorHome;
        } else if (userType === 'teacher') {
            // redirect to the teacher hub (should only get here if page was cached in the browser, because this redirect will otherwise happen in Fastly)
            window.location = secureBaseURL + 'teacher/teacher.html';
        }
    }

    const baseSearchURL = baseURL + 'donors/search.html';

    return (
        <ViewTrackingContext.Provider value={trackSectionView}>
            <div>
                <MaybeLeadGenWidget leadGenEnabled={props.leadGenEnabled} minimizedLeadGenEnabled={props.minimizedLeadGenEnabled} targetElementId={'first-section-wrapper'} />
                <HomeLayout baseSearchURL={baseSearchURL} {...props} user={user}
                    maxNumRecentDonations={MAX_PROJECTS} alternateHomeSection={alternateHomeSection}
                    detectedLocation={detectedLocation} getProjects={getProjects} loadProjects={loadProjects} />
            </div>
        </ViewTrackingContext.Provider>
    );
};

HomeController.propTypes = {
    linkToProjectCreation: PropTypes.bool.isRequired,
    showRecentDonations: PropTypes.bool.isRequired,
    leadGenEnabled: PropTypes.bool.isRequired,
    minimizedLeadGenEnabled: PropTypes.bool.isRequired,
};
HomeController = React.memo(HomeController);
export default HomeController;
